import { create } from 'zustand'
import { apiService, withApiFallback } from '../services/apiService'
import { isFeatureEnabled, log, logError } from '../config/environment'

// Type definitions
export interface ADUDimensions {
  width: number
  height: number
  depth: number
}

export interface PoolDimensions {
  width: number
  depth: number
  length: number
}

export interface ADUMaterials {
  walls: string
  roof: string
  trim: string
  door: string
  windows: string
}

export interface PoolMaterials {
  // Water and structure
  water: string
  shell: 'gunite' | 'fiberglass' | 'vinyl' | 'concrete'
  
  // Finishes
  interior: 'plaster' | 'pebble' | 'tile' | 'quartz' | 'glass_bead'
  interiorColor: string
  
  // Coping and decking
  coping: 'concrete' | 'natural_stone' | 'brick' | 'travertine' | 'granite'
  copingColor: string
  deck: 'concrete' | 'pavers' | 'natural_stone' | 'wood' | 'composite'
  deckColor: string
  
  // Tile (if applicable)
  tile?: 'ceramic' | 'glass' | 'natural_stone' | 'mosaic'
  tileColor?: string
}

export interface RoomConfig {
  id: string
  name: string
  size: { width: number; depth: number }
  features: string[]
}

export interface BathroomConfig extends RoomConfig {
  configId: string
  fixtures: string[]
}

export interface ADUFeatures {
  bedrooms: number
  bathrooms: number
  kitchen: boolean
  livingRoom: boolean
  dining: boolean
  office: boolean
  storage: boolean
  laundry: boolean
  parking: boolean
  porch: boolean
  balcony: boolean
  fireplace: boolean
}

export interface ADUSection {
  id: string
  name: string
  position: [number, number, number] // Relative to main ADU position
  dimensions: ADUDimensions
  materials: ADUMaterials
  features: ADUFeatures
  roomType?: 'living' | 'kitchen' | 'bedroom' | 'bathroom' | 'dining' | 'office' | 'storage' | 'utility'
}

export interface ADURoomConfiguration {
  bedrooms: RoomConfig[]
  bathrooms: BathroomConfig[]
  kitchen?: RoomConfig & { type: 'galley' | 'l-shape' | 'luxury' }
  livingRoom?: RoomConfig & { type: 'compact' | 'open' }
  dining?: RoomConfig
  office?: RoomConfig
  storage?: RoomConfig
}

export interface PoolFeatures {
  // Water features
  spa: boolean
  waterfall: boolean
  fountains: boolean
  
  // Lighting
  lighting: boolean
  lightingType: 'led' | 'halogen' | 'fiber_optic'
  lightingColor: 'white' | 'color_changing' | 'rgb'
  
  // Heating & cooling
  heating: boolean
  heatingType: 'gas' | 'electric' | 'solar' | 'heat_pump'
  cooling: boolean
  
  // Filtration & cleaning
  filtration: 'sand' | 'cartridge' | 'de'
  automation: 'none' | 'basic' | 'advanced' | 'smart_home'
  cleaner: 'none' | 'suction' | 'pressure' | 'robotic'
  
  // Water treatment
  sanitization: 'chlorine' | 'salt_water' | 'uv' | 'ozone'
  
  // Safety & covers
  safety: boolean
  safetyType?: 'fence' | 'cover' | 'alarm' | 'all'
  cover: boolean
  coverType?: 'manual' | 'automatic' | 'solar'
  
  // Entertainment & comfort
  sound: boolean
  wifi: boolean
  
  // Construction options
  excavation: 'standard' | 'difficult' | 'extreme'
  access: 'easy' | 'moderate' | 'difficult'
}

export type PoolShape = 'rectangular' | 'oval' | 'kidney' | 'l-shape' | 'freeform' | 'round'

export type PoolEquipmentType = 'pump' | 'filter' | 'heater' | 'electrical_panel' | 'chlorinator' | 'automation_controller'

export type ADUEquipmentType = 
  // Plumbing Fixtures
  'toilet' | 'sink_bathroom' | 'sink_kitchen' | 'shower' | 'bathtub' | 'dishwasher' | 'washing_machine' | 'dryer' |
  // Kitchen Appliances & Fixtures
  'refrigerator' | 'stove' | 'oven' | 'microwave' | 'garbage_disposal' | 'kitchen_island' | 'countertop' |
  // Cabinets & Storage
  'kitchen_cabinets' | 'bathroom_vanity' | 'closet_system' | 'pantry' | 'linen_cabinet' |
  // Windows & Doors
  'window_standard' | 'window_large' | 'sliding_door' | 'entry_door' | 'interior_door' |
  // HVAC & Utilities
  'hvac_unit' | 'electrical_panel' | 'water_heater' | 'thermostat' | 'septic_tank' | 'generator' | 'solar_panel' | 'ev_charger' |
  // Lighting & Electrical
  'ceiling_light' | 'pendant_light' | 'recessed_light' | 'outlet' | 'switch' |
  // Flooring & Finishes
  'hardwood_flooring' | 'tile_flooring' | 'carpet' | 'baseboards' | 'crown_molding'

export interface ADUEquipmentDimensions {
  width: number
  height: number
  depth: number
}

export interface ADUEquipmentMaterials {
  primary: string
  secondary: string
  accent: string
}

export interface ADUEquipmentModel {
  id: string
  type: 'ADUEquipment'
  name: string
  equipmentType: ADUEquipmentType
  position: [number, number, number]
  rotation: [number, number, number]
  scale: [number, number, number]
  dimensions: ADUEquipmentDimensions
  materials: ADUEquipmentMaterials
  aduId?: string // Optional reference to associated ADU
}

export interface PoolEquipmentDimensions {
  width: number
  height: number
  depth: number
}

export interface PoolEquipmentMaterials {
  primary: string
  secondary: string
  accent: string
}

export interface PoolEquipmentModel {
  id: string
  type: 'PoolEquipment'
  name: string
  equipmentType: PoolEquipmentType
  position: [number, number, number]
  rotation: [number, number, number]
  scale: [number, number, number]
  dimensions: PoolEquipmentDimensions
  materials: PoolEquipmentMaterials
  poolId?: string // Optional reference to associated pool
}

export interface ADUModel {
  id: string
  type: 'ADU'
  name: string
  templateId?: string
  position: [number, number, number]
  rotation: [number, number, number]
  scale: [number, number, number]
  dimensions: ADUDimensions
  materials: ADUMaterials
  features: ADUFeatures
  roomConfiguration?: ADURoomConfiguration
  sections: ADUSection[] // Multiple sections that make up this ADU
  isModular: boolean // Whether this ADU uses the modular system
}

export interface PoolModel {
  id: string
  type: 'Pool'
  name: string
  position: [number, number, number]
  rotation: [number, number, number]
  scale: [number, number, number]
  dimensions: PoolDimensions
  materials: PoolMaterials
  features: PoolFeatures
  shape: PoolShape
}

export type DesignObject = ADUModel | PoolModel | PoolEquipmentModel | ADUEquipmentModel

export interface CanvasSettings {
  width: number
  height: number
  showGrid: boolean
  gridSize: number
  backgroundImage?: string // Base64 or URL of uploaded image
  backgroundImageOpacity?: number // 0-1 for blending with 3D scene
  backgroundImageScale?: number // Scale factor for the background image
  backgroundImageMode?: 'flat' | 'cylindrical' | 'spherical' // How to display the background
  backgroundImageRotationX?: number // X-axis rotation in radians
  backgroundImageRotationY?: number // Y-axis rotation in radians  
  backgroundImageRotationZ?: number // Z-axis rotation in radians
  backgroundImageOffsetX?: number // X offset for panning
  backgroundImageOffsetY?: number // Y offset for vertical movement
  backgroundImageOffsetZ?: number // Z offset for panning
  enableRealisticMode?: boolean // Toggle for AI-enhanced realistic rendering
  enable3DTerrain?: boolean // Toggle for 3D terrain generation from image
  terrainHeightScale?: number // Height multiplier for terrain elevation
  terrainSegments?: number // Detail level of terrain mesh
  terrainBuildingAreaSize?: number // Size of flat building area (0.1-0.5)
}

export interface DesignStore {
  // State
  aduModels: ADUModel[]
  poolModels: PoolModel[]
  poolEquipment: PoolEquipmentModel[]
  aduEquipment: ADUEquipmentModel[]
  selectedObjects: DesignObject[]
  history: DesignObject[][]
  historyIndex: number
  
  // Canvas settings
  canvasSettings: CanvasSettings
  
  // Backend sync state
  currentDesignId: string | null
  isSaving: boolean
  lastSaved: Date | null
  syncStatus: 'idle' | 'syncing' | 'error' | 'offline'
  
  // Actions
  addADUModel: (adu: Omit<ADUModel, 'id'>) => void
  addPoolModel: (pool: Omit<PoolModel, 'id'>) => void
  addPoolEquipment: (equipment: Omit<PoolEquipmentModel, 'id'>) => void
  addADUEquipment: (equipment: Omit<ADUEquipmentModel, 'id'>) => void
  addADUSection: (aduId: string, section: Omit<ADUSection, 'id'>) => void
  updateADUSection: (aduId: string, sectionId: string, updates: Partial<ADUSection>) => void
  deleteADUSection: (aduId: string, sectionId: string) => void
  toggleADUModular: (aduId: string) => void
  updateObjectProperties: (id: string, properties: Partial<DesignObject>) => void
  updateADUDimensions: (id: string, dimensions: Partial<ADUDimensions>) => void
  updatePoolDimensions: (id: string, dimensions: Partial<PoolDimensions>) => void
  applyADUTemplate: (id: string, templateId: string) => void
  deleteObject: (id: string) => void
  selectObject: (id: string) => void
  clearSelection: () => void
  undo: () => void
  redo: () => void
  getAllObjects: () => DesignObject[]
  getPoolEquipmentForPool: (poolId: string) => PoolEquipmentModel[]
  getADUEquipmentForADU: (aduId: string) => ADUEquipmentModel[]
  
  // Canvas controls
  updateCanvasSettings: (settings: Partial<CanvasSettings>) => void
  setBackgroundImage: (imageUrl: string) => void
  updateBackgroundImageTransform: (transform: { scale?: number; rotationX?: number; rotationY?: number; rotationZ?: number; offsetX?: number; offsetY?: number; offsetZ?: number }) => void
  toggleRealisticMode: () => void
  toggle3DTerrain: () => void
  updateTerrainSettings: (settings: { heightScale?: number; segments?: number; buildingAreaSize?: number }) => void
  analyzeImageWithAI: (imageFile: File) => Promise<void>
  
  // Backend integration
  saveDesign: (name: string, description?: string) => Promise<string>
  loadDesign: (designId: string) => Promise<void>
  autoSave: () => Promise<void>
  syncWithBackend: () => Promise<void>
  resetDesign: () => void
}

const generateId = () => Math.random().toString(36).substr(2, 9)

export const useDesignStore = create<DesignStore>((set, get) => ({
  // Initial state
  aduModels: [],
  poolModels: [],
  poolEquipment: [],
  aduEquipment: [],
  selectedObjects: [],
  history: [],
  historyIndex: -1,
  
  // Canvas settings
  canvasSettings: {
    width: 50,
    height: 50,
    showGrid: false,
    gridSize: 5,
    backgroundImage: undefined,
    backgroundImageOpacity: 0.7,
    backgroundImageScale: 1.0,
    backgroundImageMode: 'cylindrical',
    backgroundImageRotationX: 0,
    backgroundImageRotationY: 0,
    backgroundImageRotationZ: 0,
    backgroundImageOffsetX: 0,
    backgroundImageOffsetY: 0,
    backgroundImageOffsetZ: 0,
    enableRealisticMode: false,
    enable3DTerrain: false,
    terrainHeightScale: 3, // Reduced for more subtle, buildable terrain
    terrainSegments: 64, // Reduced for better performance while maintaining detail
    terrainBuildingAreaSize: 0.25 // 25% of terrain for building area
  },
  
  // Backend sync state
  currentDesignId: null,
  isSaving: false,
  lastSaved: null,
  syncStatus: 'idle',

  // Actions
  addADUModel: (adu) => set((state) => {
    const newADU: ADUModel = {
      ...adu,
      id: generateId(),
      sections: adu.sections || [],
      isModular: adu.isModular || false
    }
    return {
      aduModels: [...state.aduModels, newADU]
    }
  }),

  addPoolModel: (pool) => set((state) => {
    const newPool: PoolModel = {
      ...pool,
      id: generateId(),
      shape: pool.shape || 'rectangular' // Default to rectangular if not specified
    }
    return {
      poolModels: [...state.poolModels, newPool]
    }
  }),

  addPoolEquipment: (equipment) => set((state) => {
    const newEquipment: PoolEquipmentModel = {
      ...equipment,
      id: generateId()
    }
    return {
      poolEquipment: [...state.poolEquipment, newEquipment]
    }
  }),

  addADUEquipment: (equipment) => set((state) => {
    const newEquipment: ADUEquipmentModel = {
      ...equipment,
      id: generateId()
    }
    return {
      aduEquipment: [...state.aduEquipment, newEquipment]
    }
  }),

  // ADU Section Management
  addADUSection: (aduId, section) => set((state) => {
    const newSection: ADUSection = {
      ...section,
      id: generateId()
    }
    
    return {
      aduModels: state.aduModels.map(adu => 
        adu.id === aduId 
          ? { ...adu, sections: [...adu.sections, newSection], isModular: true }
          : adu
      )
    }
  }),

  updateADUSection: (aduId, sectionId, updates) => set((state) => {
    return {
      aduModels: state.aduModels.map(adu => 
        adu.id === aduId 
          ? {
              ...adu,
              sections: adu.sections.map(section =>
                section.id === sectionId ? { ...section, ...updates } : section
              )
            }
          : adu
      )
    }
  }),

  deleteADUSection: (aduId, sectionId) => set((state) => {
    return {
      aduModels: state.aduModels.map(adu => 
        adu.id === aduId 
          ? {
              ...adu,
              sections: adu.sections.filter(section => section.id !== sectionId)
            }
          : adu
      )
    }
  }),

  toggleADUModular: (aduId) => set((state) => {
    return {
      aduModels: state.aduModels.map(adu => 
        adu.id === aduId 
          ? { 
              ...adu, 
              isModular: !adu.isModular,
              sections: !adu.isModular && adu.sections.length === 0 
                ? [{
                    id: generateId(),
                    name: 'Main Section',
                    position: [0, 0, 0],
                    dimensions: adu.dimensions,
                    materials: adu.materials,
                    features: adu.features,
                    roomType: 'living'
                  }]
                : adu.sections
            }
          : adu
      )
    }
  }),

  updateObjectProperties: (id, properties) => set((state) => {
    const aduIndex = state.aduModels.findIndex(adu => adu.id === id)
    if (aduIndex !== -1) {
      const updatedADUs = [...state.aduModels]
      updatedADUs[aduIndex] = { ...updatedADUs[aduIndex], ...properties as Partial<ADUModel> }
      
      // Update selectedObjects if this object is selected
      const updatedSelectedObjects = state.selectedObjects.map(obj => 
        obj.id === id ? updatedADUs[aduIndex] : obj
      )
      
      return { 
        aduModels: updatedADUs,
        selectedObjects: updatedSelectedObjects
      }
    }
    
    const poolIndex = state.poolModels.findIndex(pool => pool.id === id)
    if (poolIndex !== -1) {
      const updatedPools = [...state.poolModels]
      updatedPools[poolIndex] = { ...updatedPools[poolIndex], ...properties as Partial<PoolModel> }
      
      // Update selectedObjects if this object is selected
      const updatedSelectedObjects = state.selectedObjects.map(obj => 
        obj.id === id ? updatedPools[poolIndex] : obj
      )
      
      return { 
        poolModels: updatedPools,
        selectedObjects: updatedSelectedObjects
      }
    }
    
    const poolEquipmentIndex = state.poolEquipment.findIndex(equipment => equipment.id === id)
    if (poolEquipmentIndex !== -1) {
      const updatedPoolEquipment = [...state.poolEquipment]
      updatedPoolEquipment[poolEquipmentIndex] = { ...updatedPoolEquipment[poolEquipmentIndex], ...properties as Partial<PoolEquipmentModel> }
      
      // Update selectedObjects if this object is selected
      const updatedSelectedObjects = state.selectedObjects.map(obj => 
        obj.id === id ? updatedPoolEquipment[poolEquipmentIndex] : obj
      )
      
      return { 
        poolEquipment: updatedPoolEquipment,
        selectedObjects: updatedSelectedObjects
      }
    }
    
    const aduEquipmentIndex = state.aduEquipment.findIndex(equipment => equipment.id === id)
    if (aduEquipmentIndex !== -1) {
      const updatedADUEquipment = [...state.aduEquipment]
      updatedADUEquipment[aduEquipmentIndex] = { ...updatedADUEquipment[aduEquipmentIndex], ...properties as Partial<ADUEquipmentModel> }
      
      // Update selectedObjects if this object is selected
      const updatedSelectedObjects = state.selectedObjects.map(obj => 
        obj.id === id ? updatedADUEquipment[aduEquipmentIndex] : obj
      )
      
      return { 
        aduEquipment: updatedADUEquipment,
        selectedObjects: updatedSelectedObjects
      }
    }
    
    return state
  }),

  updateADUDimensions: (id, dimensions) => set((state) => {
    const aduIndex = state.aduModels.findIndex(adu => adu.id === id)
    if (aduIndex !== -1) {
      const updatedADUs = [...state.aduModels]
      updatedADUs[aduIndex] = { 
        ...updatedADUs[aduIndex], 
        dimensions: { ...updatedADUs[aduIndex].dimensions, ...dimensions }
      }
      
      // Update selectedObjects if this object is selected
      const updatedSelectedObjects = state.selectedObjects.map(obj => 
        obj.id === id ? updatedADUs[aduIndex] : obj
      )
      
      return { 
        aduModels: updatedADUs,
        selectedObjects: updatedSelectedObjects
      }
    }
    return state
  }),

  updatePoolDimensions: (id, dimensions) => set((state) => {
    const poolIndex = state.poolModels.findIndex(pool => pool.id === id)
    if (poolIndex !== -1) {
      const updatedPools = [...state.poolModels]
      updatedPools[poolIndex] = { 
        ...updatedPools[poolIndex], 
        dimensions: { ...updatedPools[poolIndex].dimensions, ...dimensions }
      }
      
      // Update selectedObjects if this object is selected
      const updatedSelectedObjects = state.selectedObjects.map(obj => 
        obj.id === id ? updatedPools[poolIndex] : obj
      )
      
      return { 
        poolModels: updatedPools,
        selectedObjects: updatedSelectedObjects
      }
    }
    return state
  }),

  applyADUTemplate: (id, templateId) => set((state) => {
    // This will be implemented when we import the templates
    // For now, just update the templateId
    const aduIndex = state.aduModels.findIndex(adu => adu.id === id)
    if (aduIndex !== -1) {
      const updatedADUs = [...state.aduModels]
      updatedADUs[aduIndex] = { ...updatedADUs[aduIndex], templateId }
      return { aduModels: updatedADUs }
    }
    return state
  }),

  deleteObject: (id) => set((state) => ({
    aduModels: state.aduModels.filter(adu => adu.id !== id),
    poolModels: state.poolModels.filter(pool => pool.id !== id),
    poolEquipment: state.poolEquipment.filter(equipment => equipment.id !== id),
    aduEquipment: state.aduEquipment.filter(equipment => equipment.id !== id),
    selectedObjects: state.selectedObjects.filter(obj => obj.id !== id)
  })),

  selectObject: (id) => set((state) => {
    const allObjects = [...state.aduModels, ...state.poolModels, ...state.poolEquipment, ...state.aduEquipment]
    const object = allObjects.find(obj => obj.id === id)
    if (object) {
      return { selectedObjects: [object] }
    }
    return state
  }),

  clearSelection: () => set({ selectedObjects: [] }),

  undo: () => set((state) => {
    if (state.historyIndex > 0) {
      const previousState = state.history[state.historyIndex - 1]
      return {
        historyIndex: state.historyIndex - 1,
        selectedObjects: []
      }
    }
    return state
  }),

  redo: () => set((state) => {
    if (state.historyIndex < state.history.length - 1) {
      const nextState = state.history[state.historyIndex + 1]
      return {
        historyIndex: state.historyIndex + 1,
        selectedObjects: []
      }
    }
    return state
  }),

  getAllObjects: () => {
    const state = get()
    return [...state.aduModels, ...state.poolModels, ...state.poolEquipment, ...state.aduEquipment]
  },

  getPoolEquipmentForPool: (poolId: string) => {
    const state = get()
    return state.poolEquipment.filter(equipment => equipment.poolId === poolId)
  },

  getADUEquipmentForADU: (aduId: string) => {
    const state = get()
    return state.aduEquipment.filter(equipment => equipment.aduId === aduId)
  },

  updateCanvasSettings: (settings) => set((state) => ({
    canvasSettings: { ...state.canvasSettings, ...settings }
  })),

  setBackgroundImage: (imageUrl) => set((state) => ({
    canvasSettings: { 
      ...state.canvasSettings, 
      backgroundImage: imageUrl,
      backgroundImageOpacity: 0.7,
      backgroundImageScale: 1.0,
      backgroundImageMode: 'cylindrical',
      backgroundImageRotationX: 0,
      backgroundImageRotationY: 0,
      backgroundImageRotationZ: 0,
      backgroundImageOffsetX: 0,
      backgroundImageOffsetY: 0,
      backgroundImageOffsetZ: 0,
      enable3DTerrain: true // Auto-enable 3D terrain when image is uploaded
    }
  })),

  updateBackgroundImageTransform: (transform: { 
    scale?: number; 
    rotationX?: number;
    rotationY?: number;
    rotationZ?: number;
    offsetX?: number;
    offsetY?: number;
    offsetZ?: number; 
  }) => set((state) => ({
    canvasSettings: {
      ...state.canvasSettings,
      backgroundImageScale: transform.scale ?? state.canvasSettings.backgroundImageScale,
      backgroundImageRotationX: transform.rotationX ?? state.canvasSettings.backgroundImageRotationX,
      backgroundImageRotationY: transform.rotationY ?? state.canvasSettings.backgroundImageRotationY,
      backgroundImageRotationZ: transform.rotationZ ?? state.canvasSettings.backgroundImageRotationZ,
      backgroundImageOffsetX: transform.offsetX ?? state.canvasSettings.backgroundImageOffsetX,
      backgroundImageOffsetY: transform.offsetY ?? state.canvasSettings.backgroundImageOffsetY,
      backgroundImageOffsetZ: transform.offsetZ ?? state.canvasSettings.backgroundImageOffsetZ,
    }
  })),

  toggleRealisticMode: () => set((state) => ({
    canvasSettings: { 
      ...state.canvasSettings, 
      enableRealisticMode: !state.canvasSettings.enableRealisticMode 
    }
  })),

  toggle3DTerrain: () => set((state) => ({
    canvasSettings: { 
      ...state.canvasSettings, 
      enable3DTerrain: !state.canvasSettings.enable3DTerrain 
    }
  })),

  updateTerrainSettings: (settings: { heightScale?: number; segments?: number; buildingAreaSize?: number }) => set((state) => ({
    canvasSettings: {
      ...state.canvasSettings,
      terrainHeightScale: settings.heightScale ?? state.canvasSettings.terrainHeightScale,
      terrainSegments: settings.segments ?? state.canvasSettings.terrainSegments,
      terrainBuildingAreaSize: settings.buildingAreaSize ?? state.canvasSettings.terrainBuildingAreaSize
    }
  })),

  analyzeImageWithAI: async (imageFile: File) => {
    if (!isFeatureEnabled('aiVision')) {
      log('AI Vision disabled, skipping analysis')
      return
    }

    try {
      log('Starting AI image analysis...')
      const response = await apiService.analyzeImage({
        image: imageFile,
        options: {
          detectObjects: true,
          extractDimensions: true,
          identifyStructures: true
        }
      })

      log('AI analysis completed:', response)
      
      // Apply AI insights to enhance realism
      set((state) => ({
        canvasSettings: {
          ...state.canvasSettings,
          enableRealisticMode: true
        }
      }))

    } catch (error) {
      logError('AI image analysis failed:', error)
      log('Continuing without AI analysis - 3D terrain will still work based on image data')
      
      // Don't fail the upload process - terrain generation works without AI analysis
      // The 3D terrain system uses the image directly for height mapping
    }
  },

  // Backend integration methods
  saveDesign: async (name: string, description?: string) => {
    if (!isFeatureEnabled('backendSync')) {
      log('Backend sync disabled, skipping save')
      return 'local-design'
    }

    const state = get()
    set({ isSaving: true, syncStatus: 'syncing' })

    try {
      const allObjects = state.getAllObjects()
      
      // Determine design type based on objects
      const hasADU = state.aduModels.length > 0
      const hasPool = state.poolModels.length > 0
      const designType: 'adu' | 'pool' = hasADU ? 'adu' : hasPool ? 'pool' : 'adu' // default to adu
      
      // Generate a session ID if we don't have one
      const sessionId = state.currentDesignId || `session_${Date.now()}`
      
      const saveRequest = {
        sessionId,
        designType,
        designData: {
          position: { x: 0, y: 0, z: 0 },
          rotation: 0,
          customizations: {
            name,
            description,
            objects: allObjects,
            metadata: {
              version: '1.0.0',
              createdWith: 'visualizer-frontend',
              objectCount: allObjects.length,
            }
          }
        }
      }

      let response
      if (state.currentDesignId) {
        // Update existing design
        response = await apiService.updateDesign(state.currentDesignId, saveRequest)
      } else {
        // Create new design
        response = await apiService.saveDesign(saveRequest)
      }

      set({ 
        currentDesignId: response.id,
        lastSaved: new Date(),
        syncStatus: 'idle',
        isSaving: false
      })

      log('Design saved successfully:', response.id)
      return response.id

    } catch (error) {
      logError('Failed to save design:', error)
      set({ syncStatus: 'error', isSaving: false })
      throw error
    }
  },

  loadDesign: async (designId: string) => {
    if (!isFeatureEnabled('backendSync')) {
      log('Backend sync disabled, skipping load')
      return
    }

    set({ syncStatus: 'syncing' })

    try {
      const response = await apiService.loadDesign(designId)
      
      // Debug logging
      console.log('Load Design Response:', response)
      
      // Extract objects from response (backend returns objects directly)
      const objects = (response as any).data?.designData?.customizations?.objects || response.objects || []
      console.log('Extracted objects:', objects)
      
      // Separate ADUs and Pools
      const aduModels = objects.filter((obj: any) => obj.type === 'ADU') as ADUModel[]
      const poolModels = objects.filter((obj: any) => obj.type === 'Pool') as PoolModel[]
      
      // Extract equipment if it exists
      const poolEquipment = objects.filter((obj: any) => obj.type === 'PoolEquipment') as PoolEquipmentModel[]
      const aduEquipment = objects.filter((obj: any) => obj.type === 'ADUEquipment') as ADUEquipmentModel[]
      
      console.log('Filtered objects:', { aduModels, poolModels, poolEquipment, aduEquipment })

      set({
        aduModels,
        poolModels,
        poolEquipment,
        aduEquipment,
        currentDesignId: (response as any).data?.id || response.id,
        lastSaved: new Date((response as any).data?.updatedAt || response.updatedAt),
        syncStatus: 'idle',
        selectedObjects: []
      })

      log('Design loaded successfully:', designId)

    } catch (error) {
      logError('Failed to load design:', error)
      set({ syncStatus: 'error' })
      throw error
    }
  },

  autoSave: async () => {
    const state = get()
    
    if (!state.currentDesignId || !isFeatureEnabled('backendSync')) {
      return
    }

    // Only auto-save if there are changes and it's been more than 30 seconds since last save
    const timeSinceLastSave = state.lastSaved ? Date.now() - state.lastSaved.getTime() : Infinity
    if (timeSinceLastSave < 30000) { // 30 seconds
      return
    }

    try {
      await state.saveDesign(`Auto-saved Design`, 'Automatically saved design')
      log('Auto-save completed')
    } catch (error) {
      logError('Auto-save failed:', error)
    }
  },

  syncWithBackend: async () => {
    if (!isFeatureEnabled('backendSync')) {
      return
    }

    const state = get()
    if (!state.currentDesignId) {
      return
    }

    try {
      // Reload the current design to sync with backend
      await state.loadDesign(state.currentDesignId)
      log('Sync with backend completed')
    } catch (error) {
      logError('Sync with backend failed:', error)
      set({ syncStatus: 'error' })
    }
  },

  resetDesign: () => {
    set({
      aduModels: [],
      poolModels: [],
      poolEquipment: [],
      aduEquipment: [],
      selectedObjects: [],
      history: [],
      historyIndex: -1,
      currentDesignId: null,
      lastSaved: null,
      syncStatus: 'idle'
    })
    log('Design reset')
  }
}))
